import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import {
  getMainTranscript,
  getPosition,
  getChromosome,
  getRef,
  getAlt,
} from "../../../../state/selectors/general.selectors";
import { AA_CODES } from "./Constants";

const ExplainMessage = ({
  mainTranscript,
  chromosome,
  pos,
  reference,
  alt,
}) => {
  let cPos;
  let cIntronic;
  let pValuePlain;
  let p1;
  let pPos;
  let p2;
  let hgvsCdot;
  const {
    hgvs,
    name,
    gene_symbol: geneSymbol,
    hgvs_p3: hgvsP3,
    strand,
  } = mainTranscript || {};

  if (mainTranscript && mainTranscript.hgvs) {
    const posRegex = new RegExp(/.*?(\d+)(?:-|\+)?(\d+)?.*$/i);
    // eslint-disable-next-line react/prop-types
    const cMatch = hgvs.match(posRegex);
    if (cMatch) {
      ({ 1: cPos, 2: cIntronic } = cMatch);
    }

    hgvsCdot = geneSymbol
      ? `${geneSymbol}(${name}):${hgvs}`
      : `${name}:${hgvs}`;

    if (hgvsP3) {
      pValuePlain = hgvsP3;
      const aaRegex = new RegExp(/^p.(.*?)(\d+)(.*)$/i);
      const pMatch = pValuePlain.match(aaRegex);
      if (pMatch) {
        ({ 1: p1, 2: pPos, 3: p2 } = pMatch);
        p1 = p1.replace(/[()]/g, "");
        p2 = p2.replace(/[()]/g, "");
      }
    }
  }

  const strandNotice =
    strand === "-" ? (
      <p>
        The transcript variant is different from the genomic variant because the
        transcript is on the negative strand.
      </p>
    ) : null;

  const ProteinMessage = () => {
    // chr22-39777835-T-C
    if (p2 === "=") {
      return <p>This variant does not change the protein sequence.</p>;
    }
    return (
      <p>
        {
          // chr17-7578393-A-T hg19
          p1 !== "Ter" && p2 !== "Ter" && (
            <span>
              <strong>{pValuePlain}</strong>: In the corresponding protein, in
              amino acid <strong>{pPos}</strong>, the reference sequence is a
              <strong>{` ${AA_CODES[p1]}`}</strong> and a carrier of this allele
              has a <strong>{AA_CODES[p2]}</strong>.
            </span>
          )
        }
        {p1 === "Ter" && p2 !== "Ter" && (
          <span>
            <strong>{pValuePlain}</strong>: In the corresponding protein, codon{" "}
            <strong>{pPos}</strong> is a stop codon in the reference sequence; a
            carrier of this allele has a<strong>{` ${AA_CODES[p2]}`}</strong>{" "}
            amino acid which results in a long protein.
          </span>
        )}
        {
          // chr17-7578431-G-A hg19
          p1 !== "Ter" && p2 === "Ter" && (
            <span>
              <strong>{pValuePlain}</strong>: In the corresponding protein,
              codon <strong>{pPos}</strong> the reference sequence is a
              <strong>{` ${AA_CODES[p1]}`}</strong> and a carrier of this allele
              has an early stop codon that truncates the protein at this length.
            </span>
          )
        }
      </p>
    );
  };

  return (
    <div data-testid="explain-message" className="varsome-inner-container">
      <p>
        {
          // chr5:151945072:C:G hg19
          !hgvs && (
            <span>
              In chromosome <strong>{`${chromosome?.split("chr")[1]} `}</strong>
              position <strong>{pos}</strong>, the reference sequence is
              <strong>{` ${reference}`}</strong>, and the sequence for a carrier
              of this allele is <strong>{alt}</strong>.
            </span>
          )
        }
        {hgvs && (
          <>
            {
              // chr17-7578431-G-A hg19
              hgvs.indexOf("+") === -1 && hgvs.indexOf("-") === -1 && (
                <span>
                  <strong>{hgvsCdot}</strong>: In RefSeq transcript
                  <strong>{` ${name}`}</strong> (gene
                  <strong>{` ${geneSymbol}`}</strong>) position
                  <strong>{` ${cPos}`}</strong>, the reference sequence is
                  <strong>{` ${reference}`}</strong>, and the sequence for a
                  carrier of this allele is <strong>{alt}</strong>.
                </span>
              )
            }
            {
              // chr17:7574003:A:G hg38
              hgvs.indexOf("+") === -1 && hgvs.indexOf("-") !== -1 && (
                <span>
                  <strong>{hgvsCdot}</strong>: In RefSeq transcript
                  <strong>{` ${name}`}</strong> (gene
                  <strong>{` ${geneSymbol}`}</strong>) intronic position
                  <strong>{` ${cIntronic}`}</strong> bp upstream of transcript
                  position <strong>{cPos}</strong>, the reference sequence is
                  <strong>{` ${reference}`}</strong> and the sequence for a
                  carrier of this allele is <strong>{alt}</strong>.
                </span>
              )
            }
            {
              // chr16-7578431-G-A hg19
              hgvs.indexOf("+") !== -1 && hgvs.indexOf("-") === -1 && (
                <span>
                  <strong>{hgvsCdot}</strong>: In RefSeq transcript
                  <strong>{` ${name}`}</strong> (gene
                  <strong>{` ${geneSymbol}`}</strong>) intronic position
                  <strong>{` ${cIntronic}`}</strong> bp downstream of transcript
                  position <strong>{cPos}</strong>, the reference sequence is
                  <strong>{` ${reference}`}</strong> and the sequence for a
                  carrier of this allele is <strong>{alt}</strong>.
                </span>
              )
            }
          </>
        )}
      </p>
      {pValuePlain && p1 && pPos && p2 && <ProteinMessage />}
      {strandNotice}
    </div>
  );
};

ExplainMessage.propTypes = {
  mainTranscript: PropTypes.shape({
    name: PropTypes.string,
    strand: PropTypes.string,
    coding_impact: PropTypes.string,
    function: PropTypes.arrayOf(PropTypes.string),
    hgvs: PropTypes.string,
    hgvs_p1: PropTypes.string,
    hgvs_p3: PropTypes.string,
    location: PropTypes.string,
    coding_location: PropTypes.string,
    canonical: PropTypes.bool,
    gene_symbol: PropTypes.string,
    splice_distance: PropTypes.string,
    ensembl_support_level: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
  }),
  chromosome: PropTypes.string,
  pos: PropTypes.number,
  reference: PropTypes.string,
  alt: PropTypes.string,
};

ExplainMessage.defaultProps = {
  mainTranscript: null,
  chromosome: null,
  pos: null,
  reference: null,
  alt: null,
};

const mapStateToProps = (state) => ({
  mainTranscript: getMainTranscript(state),
  chromosome: getChromosome(state),
  pos: getPosition(state),
  reference: getRef(state),
  alt: getAlt(state),
});

export default connect(mapStateToProps)(ExplainMessage);
